import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { RecognitionCount } from '../../../../types/user';
import { ResourcePath } from './ResourcePath';
import { QueryKey } from './types';

export interface LeaderboardFilters {
	hashtag?: string;
	team?: string;
	department?: string;
	location?: string;
	start_date?: string;
	end_date?: string;
	direction?: 'received' | 'given';
	limit?: number;
	offset?: number;
}

export interface LeaderboardFiltersApplied {
	hashtag: string | null;
	department: string | null;
	team: string | null;
	location: string | null;
	start_date: string | null;
	end_date: string | null;
	direction: 'received' | 'given';
	limit?: number;
	offset?: number;
}

export interface LeaderboardMeta {
	filters_applied: LeaderboardFiltersApplied;
	total_count: number;
}

export interface LeaderboardResponse {
	success: boolean;
	result: RecognitionCount[];
	meta: LeaderboardMeta;
}

export const leaderboardQueryKey = (filters?: LeaderboardFilters) => {
	const base = [QueryKey.Leaderboard];
	if (filters && Object.keys(filters).length > 0) {
		return [...base, filters];
	}
	return base;
};

export const useLeaderboard = (filters?: LeaderboardFilters) => {
	const http = useHttpClient('v2');

	// Build params object, filtering out undefined values
	const params = filters ? Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== null)) : {};

	return useQuery({
		queryKey: leaderboardQueryKey(filters),
		queryFn: () =>
			http.get<LeaderboardResponse>(ResourcePath.Leaderboard, {
				params,
			}),
		select: (data) => ({
			users: data?.data.result.filter((record) => !!record.user) ?? [],
			meta: data?.data.meta,
		}),
	});
};

export interface InfiniteLeaderboardOptions {
	initialLimit?: number;
	loadMoreLimit?: number;
}

export interface InfiniteLeaderboardData {
	users: RecognitionCount[];
	meta: LeaderboardMeta | undefined;
	totalCount: number;
}

export const infiniteLeaderboardQueryKey = (filters?: Omit<LeaderboardFilters, 'limit' | 'offset'>) => {
	const base = [QueryKey.Leaderboard, 'infinite'];
	if (filters && Object.keys(filters).length > 0) {
		return [...base, filters];
	}
	return base;
};

export const useInfiniteLeaderboard = (filters?: Omit<LeaderboardFilters, 'limit' | 'offset'>, options?: InfiniteLeaderboardOptions) => {
	const http = useHttpClient('v2');
	const initialLimit = options?.initialLimit ?? 15;
	const loadMoreLimit = options?.loadMoreLimit ?? 10;

	// Build base params object, filtering out undefined values
	const baseParams = filters ? Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== null)) : {};

	const query = useInfiniteQuery(
		infiniteLeaderboardQueryKey(filters),
		async ({ pageParam = 0 }) => {
			// First page uses initialLimit, subsequent pages use loadMoreLimit
			const isFirstPage = pageParam === 0;
			const limit = isFirstPage ? initialLimit : loadMoreLimit;

			return http.get<LeaderboardResponse>(ResourcePath.Leaderboard, {
				params: {
					...baseParams,
					limit,
					offset: pageParam,
				},
			});
		},
		{
			getNextPageParam: (lastPage, allPages) => {
				// Defensive: if response doesn't have expected structure, stop pagination
				const totalCount = lastPage?.data?.meta?.total_count;
				if (totalCount === undefined || totalCount === null) {
					return undefined;
				}

				// Calculate total items loaded so far
				const totalLoaded = allPages.reduce((sum, page) => sum + (page?.data?.result?.length ?? 0), 0);

				// If we've loaded all items, return undefined to indicate no more pages
				if (totalLoaded >= totalCount) {
					return undefined;
				}

				// Return the offset for the next page
				return totalLoaded;
			},
		}
	);

	// Transform the data for easier consumption
	const transformedData: InfiniteLeaderboardData | undefined = query.data
		? {
				users: query.data.pages.flatMap((page) => page?.data?.result?.filter((record): record is RecognitionCount => !!record.user) ?? []),
				meta: query.data.pages[0]?.data?.meta,
				totalCount: query.data.pages[0]?.data?.meta?.total_count ?? 0,
			}
		: undefined;

	return {
		...query,
		data: transformedData,
	};
};
